<template>
  <div>
    <vx-card class="mb-5">
      <heading size="h2" style-size="h4" class="mb-5">Communication and Patient Care</heading>
      <p class="mb-5">
        As either an enrolled nurse or registered nurse, one of your main tasks on a day-to-day
        basis is communication. Unlike many other jobs that require good communication, if effective
        communication is lacking, it can be life-threatening. This is why the “ISBAR framework” was
        developed.
      </p>
      <p class="mb-5">
        The ISBAR framework represents a standardised approach to communication that can be used in
        any situation. It stands for Introduction, Situation, Background, Assessment and
        Recommendation.
      </p>
      <p class="text-underline mb-5">What does this mean?</p>
      <p class="mb-5">
        Well, to understand why nurses and health care professionals use the ISBAR framework, let’s
        take a look at what we call a “Clinical Handover”, where a patient goes from one person to
        another for care. There are different types of clinical handover, such as:
      </p>

      <vs-list class="mb-10" role="list">
        <vs-list-item
          role="listitem"
          title="Escalation of deteriorating patients."
          subtitle="For example, a patient may fall and hurt themselves or their existing condition could worsen, then other health care professionals need to be involved."
        ></vs-list-item>
        <vs-list-item
          role="listitem"
          title="Patient transfers to another ward/area."
          subtitle="For example, a patient may be moved from theatre to recovery, then to a ward."
        ></vs-list-item>
        <vs-list-item
          role="listitem"
          title="Shift to shift."
          subtitle="For example, one nurse finishes their shift ready to go home and another nurse takes over."
        ></vs-list-item>
        <vs-list-item
          role="listitem"
          title="Patient transfer to a test/appointment."
          subtitle="For example: a patient may be in hospital but needs to have an X-ray, so they are transferred to that dept. and then returned."
        ></vs-list-item>
      </vs-list>

      <p class="mb-5">
        There are other types of handover, but you can already get a good idea of how often this may
        happen.<br />
        Now, this is why the ISBAR framework is so important for communication.<br />
        There are multiple documented issues worldwide in relation to ineffective clinical handover.
      </p>

      <ul style="list-style-type: '#'">
        <li>
          <p class="mb-5">
            66% of adverse events are caused by failure of communication between health
            professionals
          </p>
        </li>
        <li>
          <p class="mb-5">
            # Accurate information during clinical handover is key to ensure patient safety
          </p>
        </li>
        <li>
          <p class="mb-5">
            # Effective clinical handover can reduce communication errors between health
            professionals and improve patient safety
          </p>
        </li>
        <li>
          <p class="mb-5">
            # The use of a standardised tool to facilitate communication, both verbal and written,
            can result in efficient and effective clinical handover
          </p>
        </li>
      </ul>

      <p class="mb-5">
        <small
          ><strong>Source:</strong> Australian Commission on Safety & Quality in Health Care ‘OSSIE
          Guide to Clinical Handover Improvement’</small
        >
      </p>
      <p class="mb-5">
        Research has identified that a structured approach to handover reduces handover time and
        improves information transfer.<br />
        So how does the ISBAR Framework look?
      </p>

      <!-- This was a poster image (isbar.jpg) whose white and yellow text sat on green at
           4:1 and 3:1. me2 audit 18.3 / SC 1.4.5: the same content as real text. -->
      <div class="isbar-framework mb-5">
        <heading size="h3" style-size="h5" class="text-center mb-5"
          >Communicating within your health care team</heading
        >
        <div class="vx-row">
          <div class="vx-col w-full lg:w-1/2 mb-5">
            <heading size="h4" style-size="h5" class="isbar-framework__title"
              >Clinical Deterioration</heading
            >
            <dl class="isbar-framework__list">
              <dt>Introduction</dt>
              <dd>
                <ul class="default-list">
                  <li>Introduce yourself, your role and location</li>
                  <li>Identify the patient</li>
                </ul>
              </dd>
              <dt>Situation</dt>
              <dd>
                <ul class="default-list">
                  <li>State the immediate clinical situation</li>
                </ul>
              </dd>
              <dt>Background</dt>
              <dd>
                <ul class="default-list">
                  <li>Provide relevant clinical history and background</li>
                  <li>Presenting problems and clinical history</li>
                </ul>
              </dd>
              <dt>Assessment</dt>
              <dd>
                <ul class="default-list">
                  <li>Work through A-G physical assessment</li>
                  <li>What clinical observations are of particular concern?</li>
                  <li>What do you think the problem is?</li>
                  <li>Remember to have current observations and information ready!</li>
                </ul>
              </dd>
              <dt>Recommendation</dt>
              <dd>
                <ul class="default-list">
                  <li>What do you want the person you have called to do?</li>
                  <li>What have you done?</li>
                  <li>Be clear about what you are requesting and the timeframe</li>
                  <li>Repeat to confirm what you have heard</li>
                </ul>
              </dd>
            </dl>
          </div>
          <div class="vx-col w-full lg:w-1/2 mb-5">
            <heading size="h4" style-size="h5" class="isbar-framework__title"
              >Clinical Handover</heading
            >
            <dl class="isbar-framework__list">
              <dt>Introduction</dt>
              <dd>
                <ul class="default-list">
                  <li>Introduce yourself, your role and location</li>
                  <li>Identify team leader</li>
                  <li>Clearly identify patient and family and carer if present</li>
                </ul>
              </dd>
              <dt>Situation</dt>
              <dd>
                <ul class="default-list">
                  <li>State the immediate clinical situation</li>
                  <li>State particular issues, concerns or risks</li>
                  <li>
                    Identify risks - deteriorating patient, falls risk, allergies, limitation to
                    resuscitation
                  </li>
                </ul>
              </dd>
              <dt>Background</dt>
              <dd>
                <ul class="default-list">
                  <li>Provide relevant clinical history referring to medical record and/or eMR</li>
                </ul>
              </dd>
              <dt>Assessment</dt>
              <dd>
                <ul class="default-list">
                  <li>Work through A-G physical assessment</li>
                  <li>Refer to observations, medication and other patient charts</li>
                  <li>Summarise current risk management strategies</li>
                  <li>Have observations breached CERS criteria?</li>
                </ul>
              </dd>
              <dt>Recommendation</dt>
              <dd>
                <ul class="default-list">
                  <li>Recommendations for the shift</li>
                  <li>Refer to medical record or eMR</li>
                  <li>Provide expected date of discharge</li>
                  <li>What further assessments and actions are required by who and when</li>
                  <li>State expected frequency of observations</li>
                  <li>Request that receiver read back important actions required</li>
                </ul>
              </dd>
            </dl>
          </div>
        </div>
      </div>
      <div style="text-align: center" class="mb-10">
        <p>
          <small></small><strong>Sourced from:</strong>&nbsp;<a
            href="https://www.slhd.nsw.gov.au/btf/isbar.html"
            target="_blank"
            >www.slhd.nsw.gov.au/btf/isbar.html</a
          >
        </p>
      </div>
      <p class="mb-5">
        To see how this works in the hospital environment, click on each of the 4 videos. These
        videos show the ISBAR framework being used in different scenarios.
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <label
          class="vx-col xl:w-1/4 lg:w-1/2 w-full my-5"
          v-for="video in isbar_videos"
          :key="video.id"
        >
          <video-trigger
            :imageSrc="video.image"
            :button_id="`video-button-${video.id}`"
            @click="showVideoPopup(video.video, `video-button-${video.id}`)"
            :ariaLabel="video.title"
          />
          <p class="text-center">
            <small>{{ video.title }}</small>
          </p>
        </label>
      </div>
      <video-file-popup
        :videoURL="videoURL"
        v-if="showVideoPopupFile"
        @close="closePopupFile"
      ></video-file-popup>

      <p class="mb-5">
        Be assured that a major role in being a nurse is following procedures! If this is something
        you don’t think you would like or could be bothered learning, then perhaps nursing is not
        for you.
      </p>
      <p class="mb-5">However, once you get into the habit, it should become second nature.</p>
      <p class="mb-5">
        OK, let’s look at the ISBAR Framework for “Clinical Handover” and a couple of things in it
        that you may not understand:
      </p>

      <ul class="default-list">
        <li>e/MR. This just means an “Electronic Medical Record”</li>
        <li>
          A-G physical assessment. The A-G method is a systematic and structured approach in
          assessing a patient. A-G covers: airway, breathing, circulation, disability, exposure and
          goals.
        </li>
      </ul>
      <p class="mb-5">
        Examples of <strong>what can go wrong</strong> if this framework is not followed:
      </p>
      <ul class="default-list">
        <li>
          important background and assessment information can be missed, which may lead to a
          misdiagnosis being made, or starting a treatment that is not the best for the patient.
        </li>
        <li>
          risks may not be properly identified, such as someone being at risk of a nasty fall, and
          this puts the patient at increased risk of the incident occurring.
        </li>
        <li>
          if recommendations for treatment are not clearly communicated, the patient may have
          delayed treatment or miss out altogether, which can lead to a worsening of the patient’s
          condition.
        </li>
      </ul>
      <p class="mb-5">
        Now that we have seen the importance of communication between Health Care Professionals
        let’s consider our communication with patients.
      </p>

      <heading size="h2" style-size="h4" class="mb-5">
        Patient-Centred Care (or Person-Centred Care)
      </heading>
      <p class="mb-5">
        We must remember to consider the patient’s individual and cultural needs, their values,
        preferences, and beliefs. You may have 2 patients with the same illness or having the same
        procedure but have quite different needs.
      </p>
      <p class="mb-5">
        Patients need to be treated with dignity and compassion. They need to be seen as a person
        first, not an illness.
      </p>
      <p class="mb-5">
        We need to ensure that health care is provided to the patient in partnership with them, not
        just for them, so that they (and their carers) feel in control of their treatments and
        wellbeing.
      </p>
      <p class="mb-5">
        In any healthcare situation where communication is essential, health practitioners must
        engage professional health care interpreters for patients, families, and carers who are not
        fluent in spoken English or who are deaf.
      </p>
      <p class="mb-5">There are many benefits to, and reasons for Patient-Centred Care</p>
      <p class="mb-5">
        Watch the below videos for a nice simple overview (these are also a good example of
        Collaboration).
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <label
          class="vx-col xl:w-1/4 lg:w-1/2 w-full my-5"
          v-for="video in yt_videos"
          :key="video.id"
        >
          <video-trigger
            :imageSrc="video.image"
            :button_id="`youtube-button-${video.id}`"
            @click="showYoutubePopup(video.video, `youtube-button-${video.id}`)"
            :ariaLabel="video.title"
          />
          <p class="text-center">
            <small>{{ video.title }}</small>
          </p>
        </label>
      </div>

      <p>
        As you can see, communication and collaboration are very important Enterprise Skills (or
        Employability Skills) to develop and it is essential to follow procedures.
      </p>
    </vx-card>
  </div>
</template>

<script>
import VideoTrigger from '@/components/UI/VideoTrigger';
import VideoFilePopup from '@/components/UI/VideoFilePopup';
import store from '@/store/store';

export default {
  components: { VideoTrigger, VideoFilePopup },
  data() {
    return {
      isbar_videos: [
        {
          id: 1,
          title: 'Clinical Handover',
          image: require('@/assets/images/experiences/nurse/isbar1.png'),
          video: $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/nurse/isbar1.m4v',
        },
        {
          id: 2,
          title: 'Deteriorating Patient',
          image: require('@/assets/images/experiences/nurse/isbar2.png'),
          video: $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/nurse/isbar2.m4v',
        },
        {
          id: 3,
          title: 'Telephone Handover',
          image: require('@/assets/images/experiences/nurse/isbar3.png'),
          video: $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/nurse/isbar3.m4v',
        },
        {
          id: 4,
          title: 'ED Handover Form',
          image: require('@/assets/images/experiences/nurse/isbar4.png'),
          video: $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/nurse/isbar4.m4v',
        },
      ],
      yt_videos: [
        {
          id: 1,
          title: 'People-Centred Care',
          image: require('@/assets/images/experiences/nurse/2. People Centred Care.png'),
          video: 'https://www.youtube.com/embed/pj-AvTOdk2Q?autoplay=1',
        },
        {
          id: 2,
          title: 'Person-Centred Care',
          image: require('@/assets/images/experiences/nurse/3. Person Centred Care 2.png'),
          video: 'https://www.youtube.com/embed/NTYRtRNsAko?autoplay=1',
        },
      ],
      videoURL: false,
      showVideoPopupFile: false,
      showVideoPopupYt: false,
      ytVideoUrl: false,
    };
  },
  provide: function () {
    return {
      closePopup: this.closePopup,
    };
  },
  methods: {
    showVideoPopup(url, elemId) {
      store.dispatch('popup/showVideoPopup', url);
      store.commit('popup/setFocusElemOnPopupClose', elemId);
    },
    showYoutubePopup(url, elemId) {
      store.dispatch('popup/showIframePopup', url);
      store.commit('popup/setFocusElemOnPopupClose', elemId);
    },
    showVideoFile(videoID) {
      const video = this.isbar_videos.find((v) => v.id === videoID);
      if (video) {
        this.videoURL = video.video;
        this.showVideoPopupFile = true;
      }
    },
    closePopup() {
      if (this.showVideoPopupFile) {
        this.videoURL = false;
        this.showVideoPopupFile = false;
      } else if (this.showVideoPopupYt) {
        this.ytVideoUrl = false;
        this.showVideoPopupYt = false;
      }
    },
  },
  computed: {
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>

<style scoped>
.isbar-framework__title {
  padding: 6px 12px;
  background: #3f7d5f; /* white on it: 4.87:1 */
  color: #fff;
  border-radius: 4px;
}
.isbar-framework__list dt {
  margin-top: 12px;
  font-weight: 700;
}
.isbar-framework__list dd {
  margin: 4px 0 0 0;
}
</style>
